import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db, handleFirestoreError, OperationType } from '../../lib/firebase';
import { collection, onSnapshot, query, where, doc, deleteDoc, updateDoc, addDoc, writeBatch, setDoc, arrayUnion } from 'firebase/firestore';
import { ConventionMetadata, ConShift, ConSignup, Prop, ConventionSession, ConventionScheduleItem, UserProfile, Attendance, InventoryItem } from '../../types';
import { cleanIcsString, safeFormatISO, safeUserSearch } from '../../lib/utils';
import { format, parseISO, eachDayOfInterval } from 'date-fns';
import { toast } from 'sonner';
import { Trash2, Copy, Link, Plus, ChevronLeft, Users, Clock, Share2, AlertTriangle, Cpu, Zap, Layout, Radio, Info, Calendar, ListChecks, MapPin, ExternalLink, Download, Upload, Database, Save, Package } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import LCARSConfirmationModal from '../LCARSConfirmationModal';
import AutoScheduler from './AutoScheduler';
import Papa from 'papaparse';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';

// Similarity algorithms for name lookup suggestions
function getSimilarity(s1: string, s2: string): number {
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  if (longer.length === 0) return 1.0;
  return (longer.length - editDistance(longer, shorter)) / longer.length;
}

function editDistance(s1: string, s2: string): number {
  const s1Lower = s1.toLowerCase();
  const s2Lower = s2.toLowerCase();
  const costs: number[] = [];
  for (let i = 0; i <= s1Lower.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2Lower.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else {
        if (j > 0) {
          let newValue = costs[j - 1];
          if (s1Lower.charAt(i - 1) !== s2Lower.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
    }
    if (i > 0) {
      costs[s2Lower.length] = lastValue;
    }
  }
  return costs[s2Lower.length];
}

interface ConAdminPanelProps {
  conId: string;
  onBack: () => void;
  currentUserUid: string;
  currentUserProfile?: UserProfile | null;
}

type AdminTab = 'personnel' | 'logistics' | 'programming' | 'conflicts' | 'attendance';

export default function ConAdminPanel({ conId, onBack, currentUserUid, currentUserProfile }: ConAdminPanelProps) {
  const [convention, setConvention] = useState<ConventionMetadata | null>(null);
  const [shifts, setShifts] = useState<ConShift[]>([]);
  const [signups, setSignups] = useState<ConSignup[]>([]);
  const [props, setProps] = useState<Prop[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [allConventions, setAllConventions] = useState<ConventionMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showAutoScheduler, setShowAutoScheduler] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('personnel');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [conAttendance, setConAttendance] = useState<Attendance[]>([]);
  const [addCrewSearch, setAddCrewSearch] = useState('');
  const [attendeeVerifyName, setAttendeeVerifyName] = useState('');
  const [attendeeVerifyEmail, setAttendeeVerifyEmail] = useState('');
  const [matchingSuggestions, setMatchingSuggestions] = useState<UserProfile[]>([]);
  const [showVerifyPanel, setShowVerifyPanel] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [assigningShiftId, setAssigningShiftId] = useState<string | null>(null);
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualPass, setManualPass] = useState<'yes' | 'no'>('no');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: () => {},
    variant: 'danger'
  });

  const askConfirmation = (title: string, description: string, onConfirm: () => void, variant: 'danger' | 'info' = 'danger') => {
    setConfirmDialog({
      isOpen: true,
      title,
      description,
      onConfirm,
      variant
    });
  };

  // Helper type for conflicts
  interface ScheduleConflict {
    type: 'double_signup' | 'time_overlap' | 'similar_identity' | 'inventory_conflict';
    description: string;
    volunteerName: string;
    volunteerEmail: string;
    signups: ConSignup[];
    shifts: ConShift[];
  }

  // Calculate schedule conflicts and duplicates in real-time
  const scheduleConflicts = useMemo(() => {
    const list: ScheduleConflict[] = [];

    // Helper to check if two shifts overlap
    const doShiftsOverlap = (s1: ConShift, s2: ConShift) => {
      return s1.startTime < s2.endTime && s2.startTime < s1.endTime;
    };

    const normalize = (str?: string) => (str || '').trim().toLowerCase();

    // Group signups by volunteer identity (email, or name if email not present)
    const signupsByIdentity: Record<string, ConSignup[]> = {};
    signups.forEach(s => {
      const idKey = normalize(s.volunteerEmail) || normalize(s.volunteerName);
      if (!signupsByIdentity[idKey]) signupsByIdentity[idKey] = [];
      signupsByIdentity[idKey].push(s);
    });

    Object.entries(signupsByIdentity).forEach(([idKey, userSignups]) => {
      if (userSignups.length <= 1) return;

      // 1. Double Signups (same person, same shift)
      const signupsByShift: Record<string, ConSignup[]> = {};
      userSignups.forEach(s => {
        if (!signupsByShift[s.shiftId]) signupsByShift[s.shiftId] = [];
        signupsByShift[s.shiftId].push(s);
      });

      Object.entries(signupsByShift).forEach(([shiftId, shiftS]) => {
        if (shiftS.length > 1) {
          const shiftObj = shifts.find(sh => sh.id === shiftId);
          const propObj = shiftObj ? props.find(p => p.id === shiftObj.propId) : null;
          const shiftTime = shiftObj ? `${safeFormatISO(shiftObj.startTime, 'HH:mm', 'TBD')} - ${safeFormatISO(shiftObj.endTime, 'HH:mm', 'TBD')}` : 'TBD';
          list.push({
            type: 'double_signup',
            description: `Signed up ${shiftS.length} times for the same shift:\nDuty: ${propObj?.name || 'Unknown Station'}\nPeriod: ${shiftTime}`,
            volunteerName: shiftS[0].volunteerName,
            volunteerEmail: shiftS[0].volunteerEmail,
            signups: shiftS,
            shifts: shiftObj ? [shiftObj] : []
          });
        }
      });

      // 2. Overlapping Shifts (same person, different shifts at the same time)
      for (let i = 0; i < userSignups.length; i++) {
        for (let j = i + 1; j < userSignups.length; j++) {
          const su1 = userSignups[i];
          const su2 = userSignups[j];
          if (su1.shiftId === su2.shiftId) continue; // Double signup handles this

          const shift1 = shifts.find(sh => sh.id === su1.shiftId);
          const shift2 = shifts.find(sh => sh.id === su2.shiftId);

          if (shift1 && shift2 && doShiftsOverlap(shift1, shift2)) {
            const prop1 = props.find(p => p.id === shift1.propId);
            const prop2 = props.find(p => p.id === shift2.propId);
            const t1 = `${safeFormatISO(shift1.startTime, 'EEE HH:mm', 'TBD')} - ${safeFormatISO(shift1.endTime, 'HH:mm', 'TBD')}`;
            const t2 = `${safeFormatISO(shift2.startTime, 'EEE HH:mm', 'TBD')} - ${safeFormatISO(shift2.endTime, 'HH:mm', 'TBD')}`;
            list.push({
              type: 'time_overlap',
              description: `Time conflict! Scheduled to occupy overlapping stations:\n1. ${prop1?.name || 'Unknown'} during ${t1}\n2. ${prop2?.name || 'Unknown'} during ${t2}`,
              volunteerName: su1.volunteerName,
              volunteerEmail: su1.volunteerEmail,
              signups: [su1, su2],
              shifts: [shift1, shift2]
            });
          }
        }
      }
    });

    // 3. Identity discrepancies (Same email, different names)
    const emailsWithDifferentNames: Record<string, string[]> = {};
    signups.forEach(s => {
      const email = normalize(s.volunteerEmail);
      if (!email) return;
      if (!emailsWithDifferentNames[email]) emailsWithDifferentNames[email] = [];
      if (!emailsWithDifferentNames[email].includes(s.volunteerName)) {
        emailsWithDifferentNames[email].push(s.volunteerName);
      }
    });

    Object.entries(emailsWithDifferentNames).forEach(([email, names]) => {
      if (names.length > 1) {
        const matchingSignups = signups.filter(s => normalize(s.volunteerEmail) === email);
        list.push({
          type: 'similar_identity',
          description: `Identity discrepancy! Same email address (${email}) registered under different names: ${names.join(', ')}`,
          volunteerName: names[0],
          volunteerEmail: email,
          signups: matchingSignups,
          shifts: []
        });
      }
    });

    // 4. Identity discrepancies (Same name, different emails)
    const namesWithDifferentEmails: Record<string, string[]> = {};
    signups.forEach(s => {
      const name = normalize(s.volunteerName);
      if (!name) return;
      if (!namesWithDifferentEmails[name]) namesWithDifferentEmails[name] = [];
      const email = s.volunteerEmail ? normalize(s.volunteerEmail) : '(No Email)';
      if (!namesWithDifferentEmails[name].includes(email)) {
        namesWithDifferentEmails[name].push(email);
      }
    });

    Object.entries(namesWithDifferentEmails).forEach(([name, emails]) => {
      if (emails.length > 1) {
        const matchingSignups = signups.filter(s => normalize(s.volunteerName) === name);
        list.push({
          type: 'similar_identity',
          description: `Identity discrepancy! Normalized name "${matchingSignups[0].volunteerName}" registered with multiple emails: ${emails.join(', ')}`,
          volunteerName: matchingSignups[0].volunteerName,
          volunteerEmail: emails.join(' / '),
          signups: matchingSignups,
          shifts: []
        });
      }
    });

    // 5. Physical Asset conflicts (overlapping conventions sharing inventory items)
    const toDateStr = (d?: string) => (d ? d.slice(0, 10) : '');
    if (convention && convention.startDate && convention.endDate) {
      const c1Start = toDateStr(convention.startDate);
      const c1End = toDateStr(convention.endDate || convention.startDate);
      const c1Props = props.filter(p => convention.reservedPropIds?.includes(p.id!));
      const c1Items = c1Props.flatMap(p => p.inventoryItemIds || []);

      if (c1Items.length > 0) {
        allConventions.forEach(otherCon => {
          if (otherCon.id === convention.id) return; // Skip current convention
          if (otherCon.status === 'completed') return; // Skip completed cons
          if (!otherCon.startDate) return;

          const c2Start = toDateStr(otherCon.startDate);
          const c2End = toDateStr(otherCon.endDate || otherCon.startDate);

          // Check if dates overlap
          const overlaps = c1Start <= c2End && c2Start <= c1End;
          if (overlaps) {
            const c2Props = props.filter(p => otherCon.reservedPropIds?.includes(p.id!));
            const c2Items = c2Props.flatMap(p => p.inventoryItemIds || []);
            const sharedItemIds = c1Items.filter(id => c2Items.includes(id));

            if (sharedItemIds.length > 0) {
              const uniqueSharedIds = Array.from(new Set(sharedItemIds));
              const conflictingItemNames = uniqueSharedIds.map(id => {
                const invItem = inventory.find(item => item.id === id);
                return invItem ? invItem.name : `Unknown Item (${id})`;
              });

              list.push({
                type: 'inventory_conflict',
                description: `Physical Asset Conflict! Overlapping Convention "${otherCon.name}" (${c2Start} to ${c2End}) requires the same equipment: ${conflictingItemNames.join(', ')}`,
                volunteerName: 'N/A (System Resource)',
                volunteerEmail: '',
                signups: [],
                shifts: []
              });
            }
          }
        });
      }
    }

    return list;
  }, [signups, shifts, props, inventory, convention, allConventions]);

  // Compute props/sets that are already reserved by other overlapping conventions
  const unavailablePropIds = useMemo(() => {
    const list: string[] = [];
    const toDateStr = (d?: string) => (d ? d.slice(0, 10) : '');
    if (!convention || !convention.startDate || !convention.endDate) return list;
    const c1Start = toDateStr(convention.startDate);
    const c1End = toDateStr(convention.endDate || convention.startDate);

    allConventions.forEach(con => {
      if (con.id === convention.id) return; // Skip current convention
      if (con.status === 'completed') return; // Skip completed cons
      if (!con.startDate) return;

      const c2Start = toDateStr(con.startDate);
      const c2End = toDateStr(con.endDate || con.startDate);

      // Check if dates overlap: c1Start <= c2End && c2Start <= c1End
      const overlaps = c1Start <= c2End && c2Start <= c1End;
      if (overlaps && con.reservedPropIds) {
        con.reservedPropIds.forEach(id => {
          if (!list.includes(id)) {
            list.push(id);
          }
        });
      }
    });
    return list;
  }, [convention, allConventions]);

  useEffect(() => {
    const unsubCon = onSnapshot(doc(db, 'conventions', conId), (snap) => {
      if (snap.exists()) {
        setConvention({ id: snap.id, ...snap.data() } as ConventionMetadata);
        setLoading(false);
      }
    });

    const unsubEvent = onSnapshot(doc(db, 'events', conId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.type === 'Convention') {
           if (data.points !== 10) {
             updateDoc(doc(db, 'events', conId), { points: 10 }).catch(console.error);
           }
           setConvention(prev => {
             if (prev && !prev.ownerId?.includes('command-deck')) return prev;
             
             return {
               id: snap.id,
               name: cleanIcsString(data.name),
               startDate: data.startDate,
               endDate: data.endDate || data.startDate,
               ownerId: 'command-deck',
               status: 'active',
               dailyHours: data.dailyHours || {},
               address: cleanIcsString(data.location || ''),
               host: data.host,
               commFrequency: data.contact,
               pertinentInfo: cleanIcsString(data.pertinentInfo),
               sessions: data.sessions,
               schedule: data.schedule
             } as ConventionMetadata;
           });
           setLoading(false);
        }
      } else {
        setTimeout(() => setLoading(false), 2000);
      }
    });

    const unsubProps = onSnapshot(collection(db, 'props'), (snap) => {
      setProps(snap.docs.map(d => ({ id: d.id, ...d.data() } as Prop)));
    });

    const unsubInventory = onSnapshot(collection(db, 'inventory'), (snap) => {
      setInventory(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem)));
    });

    const unsubAllCons = onSnapshot(collection(db, 'conventions'), (snap) => {
      setAllConventions(snap.docs.map(d => ({ id: d.id, ...d.data() } as ConventionMetadata)));
    });

    const qShifts = query(collection(db, 'shifts'), where('conId', '==', conId));
    const unsubShifts = onSnapshot(qShifts, (snap) => {
      setShifts(snap.docs.map(d => ({ id: d.id, ...d.data() } as ConShift))
        .sort((a, b) => a.startTime.localeCompare(b.startTime))
      );
    });

    const qSignups = query(collection(db, 'signups'), where('conId', '==', conId));
    const unsubSignups = onSnapshot(qSignups, (snap) => {
      setSignups(snap.docs.map(d => ({ id: d.id, ...d.data() } as ConSignup)));
    });

    return () => {
      unsubCon();
      unsubEvent();
      unsubProps();
      unsubInventory();
      unsubAllCons();
      unsubShifts();
      unsubSignups();
    };
  }, [conId]);

  useEffect(() => {
    // Listen to all users for profile lookup and search selection
    const unsubUsers = onSnapshot(collection(db, 'users'), (snap) => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() } as UserProfile)));
    }, (err) => {
      console.error("Error loading users in ConAdminPanel:", err);
    });

    // Listen to attendance records for this specific convention event
    const qAttendance = query(collection(db, 'attendance'), where('eventId', '==', conId));
    const unsubAttendance = onSnapshot(qAttendance, (snap) => {
      setConAttendance(snap.docs.map(d => ({ id: d.id, ...d.data() } as Attendance)));
    }, (err) => {
      console.error("Error loading attendance in ConAdminPanel:", err);
    });

    return () => {
      unsubUsers();
      unsubAttendance();
    };
  }, [conId]);

  const handleLocalUpdate = (updates: Partial<ConventionMetadata>) => {
    setConvention(prev => prev ? { ...prev, ...updates } : null);
    setHasUnsavedChanges(true);
  };

  const handleSaveConvention = async () => {
    if (!convention || !hasUnsavedChanges) return;
    setIsUpdating(true);
    try {
      const { id, ...rawUpdates } = convention;
      const updates = Object.fromEntries(Object.entries(rawUpdates).filter(([k, v]) => v !== undefined && k !== 'id'));
      if (convention.ownerId === 'command-deck') {
        updates.ownerId = currentUserUid;
        await setDoc(doc(db, 'conventions', conId), updates);
      } else {
        await setDoc(doc(db, 'conventions', conId), updates, { merge: true });
      }
      
      if (updates.name) {
        try {
          await updateDoc(doc(db, 'events', conId), { name: updates.name });
        } catch (e) {
          console.log('Skipping event name sync:', e);
        }
      }
      
      toast.success('Tactical data synchronized');
      setHasUnsavedChanges(false);
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'conventions');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMarkCompleted = async () => {
    if (!convention) return;
    setIsUpdating(true);
    try {
      const { id, ...rawUpdates } = convention;
      const updates = Object.fromEntries(Object.entries(rawUpdates).filter(([k, v]) => v !== undefined && k !== 'id'));
      updates.status = 'completed';
      
      if (convention.ownerId === 'command-deck') {
        await updateDoc(doc(db, 'events', conId), { status: 'completed' });
      } else {
        await setDoc(doc(db, 'conventions', conId), updates, { merge: true });
      }
      
      setConvention(prev => prev ? { ...prev, status: 'completed' } : null);
      toast.success('Convention archived to historical records');
      setHasUnsavedChanges(false);
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'conventions');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExportCSV = () => {
    const csvData = shifts.map(shift => {
      const prop = props.find(p => p.id === shift.propId);
      return {
        PropName: prop?.name || 'Unknown',
        Date: shift.startTime ? safeFormatISO(shift.startTime, 'yyyy-MM-dd') : '',
        StartTime: shift.startTime ? safeFormatISO(shift.startTime, 'HH:mm') : '',
        EndTime: shift.endTime ? safeFormatISO(shift.endTime, 'HH:mm') : '',
        Capacity: shift.maxVolunteers
      };
    });

    const csvStr = Papa.unparse(csvData);
    const blob = new Blob([csvStr], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `convention_schedule_${conId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        try {
          const batch = writeBatch(db);
          let created = 0;
          const newPropsCache: Record<string, string> = {};
          const shiftCapacities: Record<string, number> = {};

          for (const row of results.data as any[]) {
            const dateStr = row.Date || row.date;
            const startStr = row['Start Time'] || row.StartTime || row.startTime;
            const endStr = row['End Time'] || row.EndTime || row.endTime;
            const volName = row['Volunteer Name'] || row.VolunteerName || '';
            const volEmail = row['Volunteer Email'] || row.VolunteerEmail || '';
            const propName = row.PropName || row.propName || 'General Duty';
            const capacity = parseInt(row.Capacity) || 1;

            if (!dateStr || !startStr || !endStr) continue;

            // Parse Date from M/D/YY or YYYY-MM-DD
            let isoDate = dateStr;
            if (dateStr.includes('/')) {
              const [m, d, y] = dateStr.split('/');
              const fullY = y.length === 2 ? `20${y}` : y;
              isoDate = `${fullY}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
            }

            // Parse Time "13:00 (1:00 PM)" or "10:30 AM"
            const extract24H = (t: string) => {
              t = t.trim();
              const ampmMatch = t.match(/(\d{1,2}:\d{2})\s*(AM|PM)/i);
              if (ampmMatch) {
                 const [_, time, modifier] = ampmMatch;
                 const parts = time.split(':');
                 let hours = parts[0];
                 const minutes = parts[1];
                 if (hours === '12') hours = '00';
                 if (modifier.toUpperCase() === 'PM') hours = (parseInt(hours, 10) + 12).toString();
                 return `${hours.padStart(2, '0')}:${minutes}`;
              }
              const match = t.match(/^(\d{1,2}:\d{2})/);
              if (match) {
                 const [hours, minutes] = match[1].split(':');
                 return `${hours.padStart(2, '0')}:${minutes}`;
              }
              return '00:00';
            };

            const startTime24 = extract24H(startStr);
            const endTime24 = extract24H(endStr);

            const lowerPropName = propName.toLowerCase();
            let propId = props.find(p => p.name.toLowerCase() === lowerPropName)?.id || newPropsCache[lowerPropName];

            if (!propId) {
               const newPropRef = doc(collection(db, 'props'));
               batch.set(newPropRef, {
                 name: propName,
                 description: 'Imported from CSV',
                 staffNeeded: capacity,
                 inventoryItemIds: []
               });
               propId = newPropRef.id;
               newPropsCache[lowerPropName] = propId;
            }

            const startTimeISO = new Date(`${isoDate}T${startTime24}:00`).toISOString();
            let endTimeISO = new Date(`${isoDate}T${endTime24}:00`).toISOString();
            
            if (endTimeISO < startTimeISO) {
                const nextDay = new Date(`${isoDate}T${endTime24}:00`);
                nextDay.setDate(nextDay.getDate() + 1);
                endTimeISO = nextDay.toISOString();
            }

            // Create or Find Shift (deduplication based on time/prop)
            // Wait, we need to find if there is already a shift created in this batch for this exact time and prop
            // We'll generate a consistent ID based on conId + propId + startTimeISO
            const shiftId = `${conId}_${propId}_${startTime24.replace(':','')}__${isoDate}`.replace(/[^a-zA-Z0-9]/g, '_');
            const shiftRef = doc(db, 'shifts', shiftId);

            // Add assigned user if present
            const assignedUserIds: string[] = [];
            
            if (volName) {
              const signupId = doc(collection(db, 'signups')).id;
              assignedUserIds.push(signupId);
              const signupRef = doc(db, 'signups', signupId);
              batch.set(signupRef, {
                shiftId,
                conId,
                volunteerName: volName,
                volunteerEmail: volEmail,
                needsBadge: 'no',
                timestamp: new Date().toISOString()
              });
            }

            const rawCap = parseInt(row.Capacity);
            if (!shiftCapacities[shiftId]) {
               shiftCapacities[shiftId] = isNaN(rawCap) ? 0 : rawCap;
            }
            if (isNaN(rawCap)) {
               shiftCapacities[shiftId] += 1;
            }

            batch.set(shiftRef, {
              conId,
              propId,
              startTime: startTimeISO,
              endTime: endTimeISO,
              assignedUserIds: arrayUnion(...assignedUserIds),
              maxVolunteers: shiftCapacities[shiftId]
            }, { merge: true }); // Use merge so multiple volunteers can be added to the same shift
            
            created++;
          }

          if (created > 0) {
            await batch.commit();
            toast.success(`${created} shifts imported successfully.`);
          } else {
            toast.error('No valid rows found in CSV.');
          }
        } catch (err: any) {
           toast.error(err.message || 'Error processing CSV');
        }
        
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      error: (err) => {
        toast.error('Failed to parse CSV: ' + err.message);
      }
    });
  };

  // Programming Helpers

  const addScheduleItem = () => {
    const newItem: ConventionScheduleItem = {
      id: Math.random().toString(36).substr(2, 9),
      time: '',
      activity: '',
    };
    handleLocalUpdate({ schedule: [...(convention?.schedule || []), newItem] });
  };

  const updateScheduleItem = (id: string, field: keyof ConventionScheduleItem, value: string) => {
    const schedule = convention?.schedule?.map(s => s.id === id ? { ...s, [field]: value } : s) || [];
    handleLocalUpdate({ schedule });
  };

  const removeScheduleItem = (id: string) => {
    const schedule = convention?.schedule?.filter(s => s.id !== id) || [];
    handleLocalUpdate({ schedule });
  };

  const handleDeleteCon = async () => {
    askConfirmation(
      'EXTERMINATE CONVENTION',
      'This will delete the convention and all associated shifts. All signup data will be lost. This action is final. Proceed?',
      async () => {
        try {
          const batch = writeBatch(db);
          shifts.forEach(s => batch.delete(doc(db, 'shifts', s.id!)));
          batch.delete(doc(db, 'conventions', conId));
          await batch.commit();
          toast.success('Convention de-materialized');
          onBack();
        } catch (err) {
          handleFirestoreError(err, OperationType.DELETE, 'conventions');
        }
      }
    );
  };

  const handleDuplicate = async () => {
    if (!convention) return;
    setIsDuplicating(true);
    try {
      const { id, ...metadata } = convention;
      const newConRef = await addDoc(collection(db, 'conventions'), {
        ...metadata,
        name: `${metadata.name} (DUPLICATE)`,
        ownerId: currentUserUid,
        createdAt: new Date().toISOString()
      });

      const batch = writeBatch(db);
      shifts.forEach(s => {
        const { id, ...shiftData } = s;
        const newShiftRef = doc(collection(db, 'shifts'));
        batch.set(newShiftRef, {
          ...shiftData,
          conId: newConRef.id,
          signupCount: 0,
          assignedUserIds: []
        });
      });
      await batch.commit();

      toast.success('Cloning process complete. Sensors locked on new convention.');
      onBack();
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'conventions');
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleRemoveVolunteerFromShift = async (shiftId: string, uid: string) => {
    const userSignup = signups.find(s => s.shiftId === shiftId && (s.id === uid || s.volunteerUid === uid));
    const name = userSignup ? userSignup.volunteerName : 'Unknown Officer';
    
    askConfirmation(
      'REMOVE VOLUNTEER',
      `Are you sure you want to remove ${name} from this shift?`,
      async () => {
        try {
          const shiftRef = doc(db, 'shifts', shiftId);
          const shift = shifts.find(s => s.id === shiftId);
          if (shift) {
            const updatedUserIds = (shift.assignedUserIds || []).filter(id => id !== uid);
            await updateDoc(shiftRef, {
              assignedUserIds: updatedUserIds
            });
          }
          
          if (userSignup && userSignup.id) {
            await deleteDoc(doc(db, 'signups', userSignup.id));
          }
          toast.success('Personnel successfully removed from shift.');
        } catch (err: any) {
          toast.error(err.message || 'Failed to remove volunteer.');
        }
      }
    );
  };

  const handleManualAssign = async (shiftId: string) => {
    if (!manualName.trim()) {
      toast.error('Officer Name is required.');
      return;
    }

    try {
      // 1. Create a signup record in firestore
      const signupId = doc(collection(db, 'signups')).id;
      const signupRef = doc(db, 'signups', signupId);
      
      const newSignup = {
        id: signupId,
        conId,
        shiftId,
        volunteerName: manualName.trim(),
        volunteerEmail: manualEmail.trim() || 'unregistered@usszavala.org',
        needsBadge: manualPass,
        timestamp: new Date().toISOString()
      };
      
      await setDoc(signupRef, newSignup);

      // 2. Add signupId to shift's assignedUserIds array
      const shiftRef = doc(db, 'shifts', shiftId);
      await updateDoc(shiftRef, {
        assignedUserIds: arrayUnion(signupId)
      });

      toast.success('Officer manually assigned to shift.');
      
      // Reset form states
      setAssigningShiftId(null);
      setManualName('');
      setManualEmail('');
      setManualPass('no');
    } catch (e: any) {
      console.error(e);
      toast.error('Failed to assign officer: ' + (e.message || 'Error'));
    }
  };

  const handleToggleAttendance = async (userId: string, isAttending: boolean) => {
    try {
      const existing = conAttendance.find(a => a.userId === userId);
      if (isAttending) {
        if (!existing) {
          await addDoc(collection(db, 'attendance'), {
            userId,
            eventId: conId,
            status: 'attending',
            timestamp: new Date().toISOString()
          });
          toast.success('Personnel marked as present.');
        }
      } else {
        if (existing && existing.id) {
          await deleteDoc(doc(db, 'attendance', existing.id));
          toast.success('Attendance record purged.');
        }
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update attendance.');
    }
  };

  const handleAddAttendeeByName = (nameInput: string) => {
    const cleanName = nameInput.trim();
    if (!cleanName) return;

    // Find exact match (case-insensitive full name comparison)
    const exactMatch = users.find(u => 
      `${u.firstName} ${u.lastName}`.toLowerCase() === cleanName.toLowerCase()
    );

    if (exactMatch) {
      handleToggleAttendance(exactMatch.uid, true);
      setAddCrewSearch('');
      toast.success(`${exactMatch.firstName} ${exactMatch.lastName} marked as present.`);
      return;
    }

    // Suggestion matching (fuzzy)
    const suggestions = users.filter(u => {
      const userFullName = `${u.firstName} ${u.lastName}`;
      const similarity = getSimilarity(userFullName, cleanName);
      
      const tokens1 = userFullName.toLowerCase().split(/\s+/);
      const tokens2 = cleanName.toLowerCase().split(/\s+/);
      const tokenMatch = tokens1.some(t => tokens2.includes(t)) || tokens2.some(t => tokens1.includes(t));
      
      return similarity > 0.45 || tokenMatch;
    }).slice(0, 5);

    setAttendeeVerifyName(cleanName);
    setAttendeeVerifyEmail('');
    setMatchingSuggestions(suggestions);
    setShowVerifyPanel(true);
  };

  const handleCreateNewUserAndMarkPresent = async () => {
    try {
      setIsUpdating(true);
      const parts = attendeeVerifyName.trim().split(/\s+/);
      const firstName = parts[0] || 'Unknown';
      const lastName = parts.slice(1).join(' ') || 'Officer';
      
      const newUserRef = doc(collection(db, 'users'));
      const newProfile: UserProfile = {
        uid: newUserRef.id,
        firstName,
        lastName,
        email: attendeeVerifyEmail.trim() || `${firstName.toLowerCase()}.${lastName.toLowerCase()}@usszavala.org`,
        zavalaJoinDate: new Date().toISOString().split('T')[0],
        ots: false,
        occ: false,
        sdfpp: false,
        roles: ['read_only'],
        manualPoints: 0,
        promotionRankDate: new Date().toISOString().split('T')[0],
        currentRank: 'COMM',
      };

      await setDoc(newUserRef, newProfile);

      await addDoc(collection(db, 'attendance'), {
        userId: newUserRef.id,
        eventId: conId,
        status: 'attending',
        timestamp: new Date().toISOString()
      });

      toast.success(`Created profile for ${firstName} ${lastName} and marked present.`);
      setShowVerifyPanel(false);
      setAttendeeVerifyName('');
      setAttendeeVerifyEmail('');
      setAddCrewSearch('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create new crew profile.');
    } finally {
      setIsUpdating(false);
    }
  };

  const attendanceCandidates = useMemo(() => {
    const list: {
      key: string;
      name: string;
      email: string;
      userId: string | null;
      isRegistered: boolean;
      isAttending: boolean;
    }[] = [];

    const addedKeys = new Set<string>();

    signups.forEach(s => {
      const email = s.volunteerEmail?.trim().toLowerCase() || '';
      const name = s.volunteerName?.trim() || '';
      const key = email || name.toLowerCase();

      if (addedKeys.has(key)) return;
      addedKeys.add(key);

      const user = users.find(u => 
        (s.volunteerUid && u.uid === s.volunteerUid) || 
        (email && u.email?.trim().toLowerCase() === email) ||
        (u.firstName + ' ' + u.lastName).trim().toLowerCase() === name.toLowerCase()
      );

      const isAttending = user ? conAttendance.some(a => a.userId === user.uid && a.status === 'attending') : false;

      list.push({
        key,
        name: user ? `${user.firstName} ${user.lastName}` : name,
        email: user ? user.email : s.volunteerEmail,
        userId: user ? user.uid : null,
        isRegistered: !!user,
        isAttending
      });
    });

    conAttendance.forEach(a => {
      if (a.status !== 'attending') return;
      const user = users.find(u => u.uid === a.userId);
      if (!user) return;

      const key = user.email?.trim().toLowerCase() || user.uid;
      if (addedKeys.has(key)) return;
      addedKeys.add(key);

      list.push({
        key,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        userId: user.uid,
        isRegistered: true,
        isAttending: true
      });
    });

    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [signups, users, conAttendance]);

  const addCrewOptions = useMemo(() => {
    if (!addCrewSearch.trim()) return [];
    return users.filter(u => {
      const matchesSearch = safeUserSearch(u, addCrewSearch);
      if (!matchesSearch) return false;

      const alreadyInList = attendanceCandidates.some(c => c.userId === u.uid);
      return !alreadyInList;
    });
  }, [users, attendanceCandidates, addCrewSearch]);

  const handleDeleteShift = async (id: string) => {
    askConfirmation(
      'Purge Shift Data',
      'Are you sure you want to remove this specific shift from the timeline? This will disqualify any assigned personnel.',
      async () => {
        try {
          await deleteDoc(doc(db, 'shifts', id));
          toast.info('Shift purged');
        } catch (err) {
          handleFirestoreError(err, OperationType.DELETE, 'shifts');
        }
      }
    );
  };

  const handleClearSchedule = async () => {
    if (shifts.length === 0) return;
    askConfirmation(
      'Purge Entire Schedule',
      'Are you absolutely sure you want to delete ALL shifts and signups for this convention? This action cannot be reversed.',
      async () => {
        try {
          const batch = writeBatch(db);
          shifts.forEach(shift => {
            if (shift.id) batch.delete(doc(db, 'shifts', shift.id));
          });
          signups.forEach(signup => {
            if (signup.id) batch.delete(doc(db, 'signups', signup.id));
          });
          await batch.commit();
          toast.success('Entire schedule purged.');
        } catch (err: any) {
          toast.error(err.message || 'Failed to clear schedule');
        }
      }
    );
  };

  const copyLink = (path: string) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const url = `${baseUrl}#${path}/${conId}`;
    navigator.clipboard.writeText(url);
    toast.success('Frequency copied to clipboard');
  };

  const openLink = (path: string) => {
    const baseUrl = window.location.href.split('#')[0];
    const url = `${baseUrl}#${path}/${conId}`;
    window.open(url, '_blank');
  };

  if (loading || !convention) {
    return (
      <div className="py-24 text-center">
        <Clock className="w-12 h-12 mx-auto animate-spin text-lcars-blue opacity-40 mb-4" />
        <p className="font-bold uppercase tracking-widest text-lcars-blue">Retrieving Operational Data...</p>
      </div>
    );
  }

  // Removed isCommandDeckPlaceholder restriction as requested by user
  // const isCommandDeckPlaceholder = convention.ownerId === 'command-deck';

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="p-3 bg-lcars-tan text-black rounded-full hover:bg-lcars-blue transition-all"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-lcars-gold uppercase tracking-tight">{convention.name}</h2>
            <p className="text-[0.7rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">Admin Tactical Interface</p>
          </div>
        </div>
        <div className="flex gap-2 w-full lg:w-auto flex-wrap">
          <button 
            onClick={handleSaveConvention}
            disabled={!hasUnsavedChanges || isUpdating}
            className={`lcars-pill flex items-center gap-2 px-6 py-2 font-bold text-xs uppercase transition-all ${hasUnsavedChanges ? 'bg-lcars-blue text-black animate-pulse' : 'bg-white/10 text-[var(--foreground)]/40'}`}
          >
            <Save className="w-4 h-4" /> {isUpdating ? 'SAVING...' : 'SAVE CHANGES'}
          </button>
          <button 
            onClick={handleDuplicate}
            disabled={isDuplicating}
            className="lcars-pill bg-lcars-pink text-black flex items-center gap-2 px-6 py-2 font-bold text-xs uppercase"
          >
            {isDuplicating ? 'CLONING...' : <><Copy className="w-4 h-4" /> DUPLICATE OPS</>}
          </button>
          <button 
            onClick={handleDeleteCon}
            className="lcars-pill bg-lcars-red text-[var(--foreground)] flex items-center gap-2 px-6 py-2 font-bold text-xs uppercase"
          >
            <Trash2 className="w-4 h-4" /> DESTRUCT
          </button>
          <button 
            onClick={() => askConfirmation(
              'ARCHIVE CONVENTION?',
              'This will mark the convention as COMPLETED and archive it. It will no longer appear on the active Con Duty Roster.',
              handleMarkCompleted,
              'info'
            )}
            className="lcars-pill bg-white/20 text-[var(--foreground)] flex items-center gap-2 px-6 py-2 font-bold text-xs uppercase hover:bg-white/30"
          >
            <Package className="w-4 h-4" /> MARK COMPLETED
          </button>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-4">
        <button 
          onClick={() => setActiveTab('personnel')}
          className={`px-6 py-2 rounded-t-lg font-bold text-xs uppercase transition-all ${activeTab === 'personnel' ? 'bg-lcars-blue text-black' : 'text-lcars-blue bg-lcars-blue/10 hover:bg-lcars-blue/20'}`}
        >
          <Users className="w-4 h-4 inline-block mr-2" /> Personnel Deployment
        </button>
        <button 
          onClick={() => setActiveTab('logistics')}
          className={`px-6 py-2 rounded-t-lg font-bold text-xs uppercase transition-all ${activeTab === 'logistics' ? 'bg-lcars-gold text-black' : 'text-lcars-gold bg-lcars-gold/10 hover:bg-lcars-gold/20'}`}
        >
          <MapPin className="w-4 h-4 inline-block mr-2" /> Mission Logistics
        </button>
        <button 
          onClick={() => setActiveTab('programming')}
          className={`px-6 py-2 rounded-t-lg font-bold text-xs uppercase transition-all ${activeTab === 'programming' ? 'bg-lcars-purple text-black' : 'text-lcars-purple bg-lcars-purple/10 hover:bg-lcars-purple/20'}`}
        >
          <ListChecks className="w-4 h-4 inline-block mr-2" /> Program Ops
        </button>
        <button 
          onClick={() => setActiveTab('conflicts')}
          className={`px-6 py-2 rounded-t-lg font-bold text-xs uppercase transition-all flex items-center ${activeTab === 'conflicts' ? 'bg-lcars-red text-[var(--foreground)]' : 'text-lcars-red bg-lcars-red/10 hover:bg-lcars-red/20'}`}
        >
          <AlertTriangle className="w-4 h-4 inline-block mr-2" /> Schedule Integrity
          {scheduleConflicts.length > 0 && (
            <span className="ml-2 bg-white text-lcars-red text-[0.6rem] font-extrabold px-1.5 py-0.5 rounded-full animate-pulse">
              {scheduleConflicts.length}
            </span>
          )}
        </button>
        <button 
          onClick={() => setActiveTab('attendance')}
          className={`px-6 py-2 rounded-t-lg font-bold text-xs uppercase transition-all ${activeTab === 'attendance' ? 'bg-lcars-blue text-black' : 'text-lcars-blue bg-lcars-blue/10 hover:bg-lcars-blue/20'}`}
        >
          <Clock className="w-4 h-4 inline-block mr-2" /> Attendance Check
        </button>
      </div>

      {activeTab === 'personnel' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-lcars-blue/10 border border-lcars-blue rounded-r-[30px] p-6 shadow-lg">
              <h3 className="text-xl font-bold text-lcars-blue uppercase mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5" /> Automation Core
              </h3>
              <p className="text-[0.65rem] text-[var(--foreground)]/50 mb-6 uppercase tracking-wider leading-relaxed">
                Optimize personnel deployment using LCARS strategic scheduling algorithms. Automatically generate shifts based on operational hours and booth requirements.
              </p>
              <button 
                onClick={() => setShowAutoScheduler(!showAutoScheduler)}
                className="w-full lcars-pill bg-lcars-blue text-black font-bold py-3 flex items-center justify-center gap-2 transition-all hover:scale-105"
              >
                <Cpu className="w-5 h-5" /> {showAutoScheduler ? 'CLOSE SCHEDULER' : 'INITIALIZE AUTO-SCHEDULER'}
              </button>
            </div>

            <div className="bg-lcars-tan/10 border border-lcars-tan rounded-r-[30px] p-6 shadow-lg">
              <h3 className="text-xl font-bold text-lcars-tan uppercase mb-6 flex items-center gap-2">
                <Share2 className="w-5 h-5" /> Subspace Links
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="text-[0.6rem] text-lcars-tan font-bold uppercase ml-1">Volunteer Signup Frequency</div>
                  <div className="flex gap-2">
                    <a 
                      href={`${window.location.origin}${window.location.pathname}#/signup/${conId}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center px-3 bg-[var(--background)] border border-lcars-tan text-[var(--foreground)] hover:text-lcars-tan text-xs h-10 hover:bg-white/5 transition-colors truncate"
                    >
                      {`${window.location.origin}${window.location.pathname}#/signup/${conId}`}
                    </a>
                    <button onClick={() => copyLink('/signup')} className="p-2 bg-lcars-tan text-black rounded hover:bg-white transition-colors" title="Copy Link"><Copy className="w-5 h-5" /></button>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-[0.6rem] text-lcars-tan font-bold uppercase ml-1">Public Schedule Matrix</div>
                  <div className="flex gap-2">
                    <a 
                      href={`${window.location.origin}${window.location.pathname}#/schedule/${conId}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center px-3 bg-[var(--background)] border border-lcars-tan text-[var(--foreground)] hover:text-lcars-tan text-xs h-10 hover:bg-white/5 transition-colors truncate"
                    >
                      {`${window.location.origin}${window.location.pathname}#/schedule/${conId}`}
                    </a>
                    <button onClick={() => copyLink('/schedule')} className="p-2 bg-lcars-tan text-black rounded hover:bg-white transition-colors" title="Copy Link"><Copy className="w-5 h-5" /></button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-lcars-blue/10 border border-lcars-blue rounded-r-[30px] p-6 shadow-lg">
              <h3 className="text-xl font-bold text-lcars-blue uppercase mb-4 flex items-center gap-2">
                <Database className="w-5 h-5" /> Data Operations
              </h3>
              <p className="text-[0.65rem] text-[var(--foreground)]/50 mb-6 uppercase tracking-wider leading-relaxed">
                Export the shift structure to a datapad or upload an updated matrix.
              </p>
              <div className="flex flex-col gap-3">
                <button 
                  onClick={handleExportCSV}
                  className="w-full rounded-full uppercase active:opacity-70 bg-[var(--background)] border border-lcars-blue text-[var(--foreground)] font-bold py-3 flex items-center justify-center gap-2 transition-all hover:bg-white/5"
                >
                  <Download className="w-4 h-4" /> EXPORT TO CSV
                </button>
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full rounded-full uppercase active:opacity-70 bg-[var(--background)] border border-lcars-blue text-[var(--foreground)] font-bold py-3 flex items-center justify-center gap-2 transition-all hover:bg-white/5"
                >
                  <Upload className="w-4 h-4" /> UPLOAD FROM CSV
                </button>
                <button 
                  onClick={handleClearSchedule}
                  disabled={shifts.length === 0}
                  className={`w-full mt-2 rounded-full uppercase active:opacity-70 border py-3 flex items-center justify-center gap-2 transition-all ${shifts.length > 0 ? 'bg-lcars-red/10 border-lcars-red text-lcars-red hover:bg-lcars-red hover:text-black font-bold' : 'bg-white/5 border-white/10 text-[var(--foreground)]/20 cursor-not-allowed'}`}
                >
                  <Trash2 className="w-4 h-4" /> PURGE ENTIRE SCHEDULE
                </button>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleImportCSV} 
                  accept=".csv" 
                  className="hidden" 
                />
              </div>
            </div>

            <div className="bg-lcars-gold/10 border border-lcars-gold rounded-r-[30px] p-6 shadow-lg">
              <h3 className="text-xl font-bold text-lcars-gold uppercase mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" /> Manning Summary
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-[var(--foreground)]/60">
                  <span className="uppercase text-[0.7rem] tracking-widest">Total Shifts</span>
                  <span className="font-bold text-[var(--foreground)] tracking-widest">{shifts.length}</span>
                </div>
                <div className="flex justify-between text-[var(--foreground)]/60">
                  <span className="uppercase text-[0.7rem] tracking-widest">Capacity</span>
                  <span className="font-bold text-[var(--foreground)] tracking-widest">{shifts.reduce((acc, s) => acc + (s.maxVolunteers || 0), 0)}</span>
                </div>
                <div className="flex justify-between text-lcars-gold font-bold text-lg pt-4 border-t border-lcars-gold/20">
                  <span className="uppercase tracking-tighter">Personnel</span>
                  <span className="tracking-widest">{shifts.reduce((acc, s) => acc + (s.assignedUserIds?.length || 0), 0)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            {showAutoScheduler && (
              <AutoScheduler 
                convention={convention} 
                existingShifts={shifts} 
                onComplete={() => setShowAutoScheduler(false)} 
              />
            )}

            <div className="flex justify-between items-center bg-lcars-blue text-black px-6 py-2 rounded-r-full shadow-inner">
              <span className="font-bold uppercase tracking-widest">Active Shift Manifest</span>
            </div>

            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 scrollbar-hide">
              {shifts.length === 0 && (
                <div className="py-20 text-center text-[var(--foreground)]/20 uppercase tracking-[0.3em] text-sm">
                   <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-10" />
                   No shifts generated
                </div>
              )}
              {shifts.map(shift => {
                const prop = props.find(p => p.id === shift.propId);
                const signupCount = shift.assignedUserIds?.length || 0;
                const maxVolunteers = shift.maxVolunteers || prop?.staffNeeded || 0;

                return (
                  <div key={shift.id} className="bg-white/5 border border-white/10 rounded-2xl py-3 px-6 flex flex-col gap-2 group hover:bg-lcars-blue/10 transition-all cursor-default">
                    <div className="flex flex-wrap lg:flex-nowrap items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-24 text-lcars-tan font-bold text-[0.65rem] uppercase leading-tight tracking-tighter">
                          {shift.startTime ? safeFormatISO(shift.startTime, 'EEE MMM dd') : 'TBD'}
                        </div>
                        <div className="w-32 text-lcars-blue font-bold text-xs tabular-nums">
                          {shift.startTime ? safeFormatISO(shift.startTime, 'HH:mm') : 'TBD'} - {shift.endTime ? safeFormatISO(shift.endTime, 'HH:mm') : 'TBD'}
                        </div>
                        <div className="w-32 text-[var(--foreground)] font-bold text-xs uppercase truncate" title={prop?.name}>
                          {prop?.name || 'General Duty'}
                        </div>
                      </div>
                      
                      <div className="flex-1 flex items-center gap-4">
                        <div className="flex-1 min-w-[120px]">
                          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-lcars-gold transition-all duration-500" 
                              style={{ width: `${Math.min(100, (signupCount / maxVolunteers) * 100)}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="w-16 text-[0.6rem] font-bold text-[var(--foreground)]/40 uppercase whitespace-nowrap tabular-nums">
                          {signupCount} / {maxVolunteers}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={() => {
                            setAssigningShiftId(assigningShiftId === shift.id ? null : shift.id!);
                            setManualName('');
                            setManualEmail('');
                            setManualPass('no');
                          }}
                          className="p-1 px-2 text-[0.65rem] bg-lcars-gold/20 text-lcars-gold hover:bg-lcars-gold hover:text-black rounded transition-colors font-bold uppercase tracking-wider"
                          title="Manually assign unregistered officer"
                        >
                          + Assign Officer
                        </button>
                        <button 
                          onClick={() => handleDeleteShift(shift.id!)}
                          className="p-2 text-[var(--foreground)]/40 hover:text-lcars-red transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {assigningShiftId === shift.id && (
                      <div className="mt-2 p-3 bg-white/5 border border-lcars-gold/20 rounded-xl space-y-3 animate-in slide-in-from-top-2 duration-300">
                        <div className="text-[0.65rem] text-lcars-gold font-bold uppercase tracking-widest">
                          Manual Shift Assignment
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input 
                            type="text"
                            placeholder="Officer Name"
                            value={manualName}
                            onChange={e => setManualName(e.target.value)}
                            className="bg-[var(--background)] border border-white/20 rounded px-2 py-1 text-xs text-[var(--foreground)] placeholder-white/40 focus:border-lcars-gold outline-none"
                          />
                          <input 
                            type="email"
                            placeholder="Email (Optional)"
                            value={manualEmail}
                            onChange={e => setManualEmail(e.target.value)}
                            className="bg-[var(--background)] border border-white/20 rounded px-2 py-1 text-xs text-[var(--foreground)] placeholder-white/40 focus:border-lcars-gold outline-none"
                          />
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-[var(--foreground)]/60">Pass Needed?</span>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input 
                                type="radio" 
                                name="manualPass" 
                                checked={manualPass === 'yes'} 
                                onChange={() => setManualPass('yes')}
                                className="accent-lcars-gold"
                              />
                              Yes
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input 
                                type="radio" 
                                name="manualPass" 
                                checked={manualPass === 'no'} 
                                onChange={() => setManualPass('no')}
                                className="accent-lcars-gold"
                              />
                              No
                            </label>
                          </div>
                          <div className="flex gap-2">
                            <button 
                              onClick={() => setAssigningShiftId(null)}
                              className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors"
                            >
                              Cancel
                            </button>
                            <button 
                              onClick={() => handleManualAssign(shift.id!)}
                              className="px-3 py-1 bg-lcars-gold text-black hover:bg-lcars-gold/80 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors"
                            >
                              Assign
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {shift.assignedUserIds && shift.assignedUserIds.length > 0 && (
                      <div className="pt-2 border-t border-white/10 text-[0.65rem] text-lcars-blue font-bold uppercase tracking-widest flex flex-wrap gap-2">
                        {shift.assignedUserIds.map(uid => {
                          const userSignup = signups.find(s => s.id === uid || s.volunteerUid === uid);
                          return (
                            <span key={uid} className="bg-[var(--background)]/40 px-2 py-1 rounded border border-white/5 flex items-center gap-1.5 group/name">
                              <span>{userSignup ? userSignup.volunteerName : 'Unknown Officer'}</span>
                              {userSignup && (
                                <span className={userSignup.needsBadge === 'yes' ? 'text-lcars-red font-bold' : 'text-lcars-tan/60 font-bold'}>
                                  {userSignup.needsBadge === 'yes' ? ' (Pass Needed)' : ' (No Pass)'}
                                </span>
                              )}
                              <button
                                onClick={() => handleRemoveVolunteerFromShift(shift.id!, uid)}
                                className="text-lcars-red/60 hover:text-lcars-red cursor-pointer transition-colors font-bold text-[0.7rem] px-1 rounded hover:bg-lcars-red/10"
                                title="Remove volunteer from shift"
                              >
                                &times;
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'logistics' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in slide-in-from-right-4">
           <div className="space-y-8">
              <div className="bg-white/5 border border-lcars-blue rounded-r-[30px] p-8 space-y-6">
                <h3 className="text-xl font-bold text-lcars-blue uppercase border-b border-lcars-blue/30 pb-2 flex items-center gap-2">
                  <Cpu className="w-5 h-5" /> Convention Identity
                </h3>
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label htmlFor="conv-name" className="text-[0.65rem] text-lcars-blue font-bold uppercase ml-2">Official Designation (Name)</label>
                    <Input 
                      id="conv-name"
                      value={convention.name || ''}
                      onChange={(e) => handleLocalUpdate({ name: e.target.value })}
                      className="bg-[var(--background)]/50 border-lcars-blue text-[var(--foreground)]"
                      placeholder="Enter convention name..."
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-lcars-gold rounded-r-[30px] p-8 space-y-6">
                <h3 className="text-xl font-bold text-lcars-gold uppercase border-b border-lcars-gold/30 pb-2 flex items-center gap-2">
                  <MapPin className="w-5 h-5" /> Spatial Logistics
                </h3>
              <div className="space-y-4">
                <div className="space-y-1">
                  <div className="flex justify-between items-center mr-2">
                    <label htmlFor="conv-address" className="text-[0.65rem] text-lcars-gold font-bold uppercase ml-2">Coordinates / Address</label>
                    {convention.address && (
                      <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(convention.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[0.6rem] text-lcars-blue font-bold uppercase hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" /> View on Maps
                      </a>
                    )}
                  </div>
                  <Input 
                    id="conv-address"
                    value={convention.address || ''}
                    onChange={(e) => handleLocalUpdate({ address: e.target.value })}
                    className="bg-[var(--background)]/50 border-lcars-gold text-[var(--foreground)]"
                    placeholder="Enter convention location..."
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="conv-website" className="text-[0.65rem] text-lcars-gold font-bold uppercase ml-2">Subspace Link (Website)</label>
                  <Input 
                    id="conv-website"
                    value={convention.website || ''}
                    onChange={(e) => handleLocalUpdate({ website: e.target.value })}
                    className="bg-[var(--background)]/50 border-lcars-gold text-[var(--foreground)]"
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="conv-volunteerCommLink" className="text-[0.65rem] text-lcars-gold font-bold uppercase ml-2 flex items-center gap-1">
                    <Radio className="w-3 h-3" /> Volunteer Comms Link
                  </label>
                  <Input 
                    id="conv-volunteerCommLink"
                    value={convention.volunteerCommLink || ''} 
                    onChange={(e) => handleLocalUpdate({ volunteerCommLink: e.target.value })}
                    className="bg-[var(--background)]/50 border-lcars-gold text-[var(--foreground)]" 
                    placeholder="e.g. Discord, Slack, WhatsApp group..."
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="conv-photoAlbumLink" className="text-[0.65rem] text-lcars-gold font-bold uppercase ml-2">Photo Album Link (QR)</label>
                  <Input 
                    id="conv-photoAlbumLink"
                    value={convention.photoAlbumLink || ''} 
                    onChange={(e) => handleLocalUpdate({ photoAlbumLink: e.target.value })}
                    className="bg-[var(--background)]/50 border-lcars-gold text-[var(--foreground)]" 
                    placeholder="e.g. Google Photos, iCloud link..."
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="conv-boothSize" className="text-[0.65rem] text-lcars-gold font-bold uppercase ml-2">Booth Dimensions</label>
                  <Input 
                    id="conv-boothSize"
                    value={convention.boothSize || ''}
                    onChange={(e) => handleLocalUpdate({ boothSize: e.target.value })}
                    className="bg-[var(--background)]/50 border-lcars-gold text-[var(--foreground)]"
                    placeholder="e.g. 10x20 Endcap"
                  />
                </div>
              </div>

              <h3 className="text-xl font-bold text-lcars-blue uppercase border-b border-lcars-blue/30 pt-4 pb-2 flex items-center gap-2">
                <Radio className="w-5 h-5" /> Mission Command
              </h3>
              <div className="space-y-4">
                <div className="space-y-1">
                  <label htmlFor="conv-host" className="text-[0.65rem] text-lcars-blue font-bold uppercase ml-2">Mission Host / Commander</label>
                  <Input 
                    id="conv-host"
                    value={convention.host || ''}
                    onChange={(e) => handleLocalUpdate({ host: e.target.value })}
                    className="bg-[var(--background)]/50 border-lcars-blue text-[var(--foreground)]"
                    placeholder="e.g. Admiral Christopher Pike"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="conv-commFreq" className="text-[0.65rem] text-lcars-blue font-bold uppercase ml-2">Comm Frequency (Contact)</label>
                  <Input 
                    id="conv-commFreq"
                    value={convention.commFrequency || convention.helpContact || ''}
                    onChange={(e) => handleLocalUpdate({ commFrequency: e.target.value })}
                    className="bg-[var(--background)]/50 border-lcars-blue text-[var(--foreground)]"
                    placeholder="Contact email or phone..."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-lcars-tan rounded-r-[30px] p-8 space-y-6">
              <h3 className="text-xl font-bold text-lcars-tan uppercase border-b border-lcars-tan/30 pb-2 flex items-center gap-2">
                <Info className="w-5 h-5" /> Pertinent Mission Info
              </h3>
              <div className="space-y-6">
                <Textarea 
                  value={convention.pertinentInfo || ''}
                  onChange={(e) => handleLocalUpdate({ pertinentInfo: e.target.value })}
                  className="bg-[var(--background)]/50 border-white/20 text-[var(--foreground)] min-h-[200px] text-sm leading-relaxed scrollbar-hide"
                  placeholder="Operational notes, special instructions, and critical briefings..."
                />
                
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-lcars-tan uppercase border-b border-lcars-tan/20 pb-1">Opening Frequencies (Daily Hours)</h4>
                  <div className="grid grid-cols-1 gap-3 max-h-[300px] overflow-y-auto pr-2 scrollbar-hide">
                    {(convention.startDate && convention.endDate && !isNaN(parseISO(convention.startDate).getTime()) && !isNaN(parseISO(convention.endDate).getTime())) ? eachDayOfInterval({ 
                      start: parseISO(convention.startDate), 
                      end: parseISO(convention.endDate) 
                    }).map(date => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      return (
                        <div key={dateStr} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 bg-[var(--background)]/30 p-3 rounded-lg border border-white/5">
                          <div className="w-full sm:w-32 font-bold text-lcars-tan text-[0.65rem] uppercase">{format(date, 'EEE, MMM dd')}</div>
                          <div className="flex flex-1 items-center gap-2 w-full">
                            <Input 
                              type="time" 
                              value={convention.dailyHours?.[dateStr]?.open || ''}
                              className="bg-[var(--background)] border-lcars-tan h-8 text-[0.7rem] tabular-nums w-full"
                              onChange={(e) => handleLocalUpdate({
                                dailyHours: { 
                                  ...convention.dailyHours, 
                                  [dateStr]: { ...convention.dailyHours?.[dateStr], open: e.target.value } 
                                }
                              })}
                            />
                            <span className="text-[0.6rem] opacity-40 font-bold tracking-tighter">—</span>
                            <Input 
                              type="time" 
                              value={convention.dailyHours?.[dateStr]?.close || ''}
                              className="bg-[var(--background)] border-lcars-tan h-8 text-[0.7rem] tabular-nums w-full"
                              onChange={(e) => handleLocalUpdate({
                                dailyHours: { 
                                  ...convention.dailyHours, 
                                  [dateStr]: { ...convention.dailyHours?.[dateStr], close: e.target.value } 
                                }
                              })}
                            />
                          </div>
                        </div>
                      );
                    }) : <div className="text-[var(--foreground)]/40 text-[0.7em] uppercase tracking-widest font-bold">Specify start and end dates first.</div>}
                  </div>
                </div>
              </div>
              <div className="bg-white/5 p-4 rounded-lg flex items-center gap-3">
                 <Radio className="w-5 h-5 text-lcars-tan opacity-50" />
                 <p className="text-[0.6rem] text-[var(--foreground)]/40 uppercase tracking-widest leading-normal">
                   Updates to logistics parameters are synchronized instantly with the volunteer tactical terminal and public matrix.
                 </p>
              </div>
           </div>
        </div>
      )}

      {activeTab === 'programming' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in slide-in-from-right-4">
           <div className="space-y-6">
              <div className="flex justify-between items-center border-b border-lcars-blue/30 pb-2">
                <h3 className="text-xl font-bold text-lcars-blue uppercase flex items-center gap-2">
                   <Layout className="w-5 h-5" /> Assigned Scenes
                </h3>
              </div>
              <p className="text-sm text-[var(--foreground)]/60 mb-4">
                Select the global scenes deployed for this mission. The auto-scheduler will map these scenes for the duration of the convention.
              </p>
              
              <div className="grid grid-cols-1 gap-3 max-h-[500px] overflow-y-auto pr-2 scrollbar-hide">
                {props.length === 0 && (
                  <p className="text-[var(--foreground)]/20 text-[0.7rem] uppercase tracking-widest text-center py-12">No global scenes in inventory</p>
                )}
                {props.map(prop => {
                  if (prop.name === 'General Convention Duty') return null; // Skip general duty
                  const isSelected = convention.reservedPropIds?.includes(prop.id!);
                  const isUnavailable = unavailablePropIds.includes(prop.id!);
                  return (
                    <button 
                      key={prop.id}
                      onClick={() => {
                        if (isUnavailable) {
                          toast.error(`Set "${prop.name}" is already reserved by another overlapping convention.`);
                          return;
                        }
                        const current = convention.reservedPropIds || [];
                        const updated = isSelected 
                          ? current.filter(id => id !== prop.id)
                          : [...current, prop.id!];
                        handleLocalUpdate({ reservedPropIds: updated });
                      }}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        isUnavailable
                          ? 'border-lcars-red/30 bg-lcars-red/5 opacity-50 cursor-not-allowed'
                          : isSelected 
                            ? 'bg-lcars-blue/20 border-lcars-blue text-[var(--foreground)]' 
                            : 'bg-white/5 border-white/10 text-[var(--foreground)]/50 hover:border-white/30'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-bold uppercase text-sm truncate pr-2">{prop.name}</span>
                        {isUnavailable ? (
                          <span className="bg-lcars-red text-[var(--foreground)] text-[0.55rem] font-bold px-1.5 py-0.5 rounded uppercase shrink-0">
                            RESERVED
                          </span>
                        ) : isSelected ? (
                          <div className="w-2 h-2 rounded-full bg-lcars-blue mt-1.5 shrink-0 shadow-[0_0_8px_rgba(153,204,255,0.8)]" />
                        ) : null}
                      </div>
                      <div className="text-[0.65rem] uppercase tracking-widest opacity-60">
                        {prop.staffNeeded} Staff Required / Shift
                      </div>
                    </button>
                  );
                })}
              </div>
           </div>

           <div className="space-y-6">
              <div className="flex justify-between items-center border-b border-lcars-purple/30 pb-2">
                <h3 className="text-xl font-bold text-lcars-purple uppercase flex items-center gap-2">
                   <ListChecks className="w-5 h-5" /> Operational Schedule
                </h3>
                <button 
                  onClick={addScheduleItem}
                  className="lcars-pill bg-lcars-purple text-black px-4 py-1 text-[0.6rem] font-bold flex items-center gap-1 hover:bg-white transition-all"
                >
                  <Plus className="w-3 h-3" /> ADD ITEM
                </button>
              </div>
              
              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 scrollbar-hide">
                {convention.schedule?.length === 0 && (
                  <p className="text-[var(--foreground)]/20 text-[0.7rem] uppercase tracking-widest text-center py-12">No schedule items defined</p>
                )}
                {convention.schedule?.map(item => (
                   <div key={item.id} className="bg-white/5 border border-white/10 p-4 rounded-xl space-y-3 group relative hover:border-lcars-purple/30 transition-all">
                      <button 
                         onClick={() => removeScheduleItem(item.id)}
                         className="absolute top-2 right-2 p-2 text-[var(--foreground)]/20 hover:text-lcars-red transition-colors lg:opacity-0 lg:group-hover:opacity-100"
                      >
                         <Trash2 className="w-4 h-4" />
                      </button>
                      <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-4 space-y-1">
                          <label htmlFor={`sched-time-${item.id}`} className="text-[0.6rem] text-[var(--foreground)]/30 font-bold uppercase ml-1">Temporal Offset</label>
                          <Input 
                            id={`sched-time-${item.id}`}
                            value={item.time || ''}
                            onChange={(e) => updateScheduleItem(item.id, 'time', e.target.value)}
                            className="bg-[var(--background)]/50 border-white/20 h-8 text-xs font-mono"
                            placeholder="09:00 / Day 1"
                          />
                        </div>
                        <div className="col-span-8 space-y-1">
                          <label htmlFor={`sched-activity-${item.id}`} className="text-[0.6rem] text-[var(--foreground)]/30 font-bold uppercase ml-1">Designated Activity</label>
                          <Input 
                            id={`sched-activity-${item.id}`}
                            value={item.activity || ''}
                            onChange={(e) => updateScheduleItem(item.id, 'activity', e.target.value)}
                            className="bg-[var(--background)]/50 border-white/20 h-8 text-xs"
                            placeholder="Main Stage Opening..."
                          />
                        </div>
                      </div>
                   </div>
                ))}
              </div>
              <div className="bg-lcars-purple/10 p-4 rounded-lg border border-lcars-purple/20">
                 <p className="text-[0.6rem] text-lcars-purple font-bold uppercase italic leading-relaxed">
                   Operational Schedule is utilized for public-facing timelines and tactical coordination between departments.
                 </p>
              </div>
           </div>
        </div>
      )}

      {activeTab === 'conflicts' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="bg-lcars-red/10 border border-lcars-red rounded-r-[30px] p-6 shadow-lg">
            <h3 className="text-xl font-bold text-lcars-red uppercase mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 animate-pulse" /> Subspace Integrity Assessment
            </h3>
            <p className="text-[0.65rem] text-[var(--foreground)]/50 mb-4 uppercase tracking-wider leading-relaxed">
              Scan for scheduling conflicts, duplicate signups, and identity discrepancies. If an officer is scheduled to occupy two stations simultaneously, or is signed up twice for the same station, the system will raise an alert below.
            </p>
          </div>

          <div className="space-y-4">
            {scheduleConflicts.length === 0 ? (
              <div className="bg-white/5 border border-white/10 rounded-[30px] p-8 text-center">
                <Users className="w-12 h-12 mx-auto text-lcars-blue opacity-40 mb-4" />
                <p className="text-lg font-bold text-lcars-blue uppercase tracking-widest">NO CONFLICTS DETECTED</p>
                <p className="text-xs text-[var(--foreground)]/40 mt-1 uppercase">All personnel deployment paths are clear and aligned.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {scheduleConflicts.map((conflict, idx) => {
                  let badgeColor = 'bg-lcars-red text-[var(--foreground)]';
                  if (conflict.type === 'double_signup') badgeColor = 'bg-lcars-orange text-black';
                  if (conflict.type === 'similar_identity') badgeColor = 'bg-lcars-gold text-black';

                  return (
                    <div key={idx} className="bg-white/5 border border-white/20 rounded-[30px] p-6 relative overflow-hidden flex flex-col justify-between hover:border-white/30 transition-colors">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-white/5 rounded-bl-[30px] pointer-events-none" />
                      <div>
                        <div className="flex justify-between items-start mb-4">
                          <span className={`${badgeColor} px-3 py-1 rounded-[10px] text-[0.65rem] font-bold uppercase tracking-wider`}>
                            {conflict.type.replace('_', ' ')}
                          </span>
                          <span className="text-[0.65rem] text-[var(--foreground)]/40 font-bold uppercase tabular-nums">
                            ALERT #{idx + 1}
                          </span>
                        </div>
                        <h4 className="font-bold text-lg text-[var(--foreground)] uppercase tracking-tight mb-2">
                          {conflict.type === 'inventory_conflict' ? 'Physical Asset Conflict' : conflict.volunteerName}
                        </h4>
                        {conflict.volunteerEmail && (
                          <p className="text-xs text-[var(--foreground)]/60 font-bold mb-4 uppercase tracking-wider">
                            Comm: {conflict.volunteerEmail}
                          </p>
                        )}
                        <div className="bg-[var(--background)]/40 border border-white/10 rounded-xl p-4 mb-4 whitespace-pre-line text-xs font-bold text-lcars-tan leading-relaxed">
                          {conflict.description}
                        </div>
                      </div>

                      {conflict.signups.length > 0 && (
                        <div className="space-y-2 mt-4 border-t border-white/10 pt-4">
                          <div className="text-[0.6rem] text-[var(--foreground)]/40 font-bold uppercase mb-2">Resolve Conflict (Prune Signups)</div>
                          {conflict.signups.map((signup) => {
                            const shift = shifts.find(s => s.id === signup.shiftId);
                            const prop = shift ? props.find(p => p.id === shift.propId) : null;
                            return (
                              <div key={signup.id} className="flex items-center justify-between bg-[var(--background)]/60 rounded-xl p-3 border border-white/5 gap-4">
                                <div className="text-[0.7rem] uppercase font-bold text-[var(--foreground)]/80 truncate">
                                  <div className="truncate">{prop?.name || 'Unknown Duty'}</div>
                                  <div className="text-[0.6rem] text-[var(--foreground)]/40 mt-0.5 font-mono">
                                    {shift ? `${safeFormatISO(shift.startTime, 'EEE MMM dd HH:mm', 'TBD')}` : 'Time TBD'}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveVolunteerFromShift(signup.shiftId, signup.volunteerUid || signup.id!)}
                                  className="lcars-pill bg-lcars-red text-[var(--foreground)] text-[0.6rem] px-3 py-1 font-bold uppercase hover:bg-lcars-red/80 transition-colors shrink-0"
                                >
                                  REMOVE
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'attendance' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="bg-lcars-blue/10 border border-lcars-blue rounded-r-[30px] p-6 shadow-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="max-w-xl">
              <h3 className="text-xl font-bold text-lcars-blue uppercase mb-2 flex items-center gap-2">
                <Clock className="w-5 h-5" /> Convention Attendance Roster
              </h3>
              <p className="text-[0.65rem] text-[var(--foreground)]/50 uppercase tracking-wider leading-relaxed">
                Confirm which crew members attended this convention operation. Attending personnel will automatically receive 10 points. Check their names to mark as present.
              </p>
            </div>
            
            <div className="relative w-full md:w-[420px]">
              <label htmlFor="crew-search-input" className="text-[0.6rem] text-lcars-blue font-bold uppercase ml-1 block mb-1">Add Crew Member / Search Name</label>
              <div className="flex gap-2">
                <Input
                  id="crew-search-input"
                  placeholder="ENTER NAME OR EMAIL..."
                  value={addCrewSearch}
                  onChange={(e) => setAddCrewSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddAttendeeByName(addCrewSearch);
                    }
                  }}
                  className="bg-[var(--background)]/50 border-lcars-blue text-xs uppercase flex-1"
                />
                <button
                  onClick={() => handleAddAttendeeByName(addCrewSearch)}
                  className="lcars-pill bg-lcars-blue text-black px-4 py-1.5 text-[0.65rem] font-bold uppercase whitespace-nowrap hover:bg-white transition-colors"
                >
                  Lookup / Add
                </button>
              </div>
              {addCrewOptions.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-[var(--background)] border border-lcars-blue/50 rounded-b-xl z-50 max-h-40 overflow-y-auto divide-y divide-white/5 shadow-2xl">
                  {addCrewOptions.map(u => (
                    <button
                      key={u.uid}
                      onClick={() => {
                        handleToggleAttendance(u.uid, true);
                        setAddCrewSearch('');
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-lcars-blue/10 text-xs font-bold text-[var(--foreground)] uppercase truncate flex justify-between items-center"
                    >
                      <span>{u.firstName} {u.lastName}</span>
                      <span className="text-[0.6rem] text-lcars-blue">{u.currentRank}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {showVerifyPanel && (
            <div className="bg-lcars-orange/15 border border-lcars-orange rounded-r-[30px] p-6 space-y-4 animate-in slide-in-from-top-2 border-l-8">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-base font-bold text-lcars-orange uppercase tracking-wide">Verify Attendee Identity</h4>
                  <p className="text-[0.65rem] text-[var(--foreground)]/50 uppercase tracking-widest">No exact database match found for: "{attendeeVerifyName}"</p>
                </div>
                <button 
                  onClick={() => setShowVerifyPanel(false)}
                  className="text-[var(--foreground)]/40 hover:text-lcars-red uppercase font-bold text-[0.65rem]"
                >
                  [ Abort ]
                </button>
              </div>

              {matchingSuggestions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-lcars-orange font-bold uppercase tracking-wider">Did you mean one of these crew members?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {matchingSuggestions.map(u => (
                      <div key={u.uid} className="flex justify-between items-center bg-[var(--background)]/45 p-3 rounded-lg border border-white/5">
                        <span className="text-xs text-[var(--foreground)] font-bold uppercase">{u.firstName} {u.lastName} ({u.currentRank})</span>
                        <button
                          onClick={() => {
                            handleToggleAttendance(u.uid, true);
                            setShowVerifyPanel(false);
                            setAddCrewSearch('');
                          }}
                          className="lcars-pill bg-lcars-orange text-black text-[0.6rem] px-3 py-1 font-bold uppercase hover:bg-white transition-colors"
                        >
                          Select
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-white/10 pt-4 space-y-3">
                <p className="text-xs text-lcars-orange font-bold uppercase tracking-wider">Or, enlist them as a new crew member:</p>
                <div className="flex flex-col sm:flex-row gap-3 items-end">
                  <div className="flex-1 space-y-1 w-full">
                    <label htmlFor="verify-email-input" className="text-[0.6rem] text-lcars-orange font-bold uppercase ml-1 block">New Crew Email Address (Optional)</label>
                    <Input
                      id="verify-email-input"
                      placeholder="ENTER EMAIL ADDRESS..."
                      value={attendeeVerifyEmail}
                      onChange={(e) => setAttendeeVerifyEmail(e.target.value)}
                      className="bg-[var(--background)]/50 border-lcars-orange text-xs text-[var(--foreground)] uppercase"
                    />
                  </div>
                  <button
                    onClick={handleCreateNewUserAndMarkPresent}
                    disabled={isUpdating}
                    className="lcars-pill bg-lcars-orange text-black px-6 py-2 text-xs font-bold uppercase whitespace-nowrap hover:bg-white transition-all w-full sm:w-auto text-center"
                  >
                    {isUpdating ? 'Enlisting...' : 'Enlist Crew & Mark Present'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white/5 border border-white/20 rounded-[30px] p-6">
            {attendanceCandidates.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-[var(--foreground)]/20 mb-4" />
                <p className="text-lg font-bold text-[var(--foreground)]/40 uppercase tracking-widest">No candidates available</p>
                <p className="text-xs text-[var(--foreground)]/30 mt-1 uppercase">Use the crew search box above to check in personnel.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="text-lcars-blue text-[0.7rem] font-bold tracking-widest uppercase border-b-2 border-lcars-blue/30">
                      <th className="text-left p-4 w-24">ATTENDED</th>
                      <th className="text-left p-4">NAME</th>
                      <th className="text-left p-4">EMAIL</th>
                      <th className="text-left p-4">REGISTRATION STATUS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10 font-bold uppercase tracking-wider text-xs">
                    {attendanceCandidates.map((c) => (
                      <tr key={c.key} className="hover:bg-white/5 transition-colors">
                        <td className="p-4">
                          <input
                            type="checkbox"
                            disabled={!c.isRegistered}
                            checked={c.isAttending}
                            onChange={(e) => {
                              if (c.userId) {
                                handleToggleAttendance(c.userId, e.target.checked);
                              }
                            }}
                            className="w-5 h-5 accent-lcars-blue cursor-pointer disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="p-4 text-base font-bold text-[var(--foreground)]">{c.name}</td>
                        <td className="p-4 text-[var(--foreground)]/60 font-mono">{c.email || 'N/A'}</td>
                        <td className="p-4 text-[0.7rem]">
                          {c.isRegistered ? (
                            <span className="bg-lcars-blue/25 text-lcars-blue px-3 py-1 rounded-[8px] text-[0.65rem] border border-lcars-blue/45">
                              Registered Crew
                            </span>
                          ) : (
                            <span className="bg-lcars-red/25 text-lcars-red px-3 py-1 rounded-[8px] text-[0.65rem] border border-lcars-red/45" title="Guests must register a user account to track promotion points.">
                              Guest (No Points Logged)
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <LCARSConfirmationModal
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant={confirmDialog.variant}
      />
    </div>
  );
}
